import { useState, type FormEvent } from 'react';
import { pb } from '../lib/pb';
import { startGoogleLogin } from '../lib/googleAuth';
import { TEAM_EMAIL_DOMAIN } from '../lib/roster';

/* Sign-in. Google is the primary path because everyone on the team already has a Decked
   Google account; email + password stays as a fallback for anyone whose Google sign-in is
   misbehaving, and for automated checks.

   This screen only obtains a PocketBase session. Whether that session is ALLOWED into the app
   is decided separately by the roster gate (see lib/roster.ts), so a successful sign-in here
   can still be refused a moment later — which is why the refusal message lives in App, not
   here. */

interface Props {
  /** Set when a previous attempt was refused, so the person is not left guessing. */
  notice?: string | null;
  noticeIsTransient?: boolean;
  onRetry?: () => void;
}

export default function LoginScreen({ notice, noticeIsTransient, onRetry }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<'google' | 'password' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  async function handleGoogle() {
    setError(null);
    setBusy('google');
    const result = await startGoogleLogin();
    if (!result.ok) {
      setError(result.error || 'Google sign-in failed.');
      setBusy(null);
    }
    // On success the browser is already navigating to Google; leave the button disabled.
  }

  async function handlePassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy('password');
    try {
      await pb.collection('users').authWithPassword(email.trim(), password);
      // useAuth picks this up via pb.authStore.onChange and runs the roster gate.
    } catch {
      setError('That email and password did not match an account.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div>
          <div className="wordmark">Adorian</div>
          <div className="product">Real or Fake</div>
        </div>

        <p className="lede">
          Build headline series and hand the channel a link to play them. Sign in with your
          @{TEAM_EMAIL_DOMAIN} account.
        </p>

        {notice && (
          <div className={`notice${noticeIsTransient ? '' : ' bad'}`}>
            {notice}
            {noticeIsTransient && onRetry && (
              <>
                {' '}
                <button className="btn ghost" style={{ padding: '4px 10px', marginTop: 8 }} onClick={onRetry}>
                  Try Again
                </button>
              </>
            )}
          </div>
        )}

        <button className="google-btn" onClick={handleGoogle} disabled={busy !== null}>
          <svg viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.7 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.6-.15-3.15-.42-4.65H24v9.02h12.94c-.56 2.9-2.2 5.36-4.7 7.02l7.6 5.9c4.44-4.1 7.14-10.14 7.14-17.29z" />
            <path fill="#FBBC05" d="M10.4 28.7a14.4 14.4 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1z" />
            <path fill="#34A853" d="M24 48c6.2 0 11.4-2.05 15.2-5.56l-7.6-5.9c-2.1 1.42-4.8 2.26-7.6 2.26-6.3 0-11.7-3.7-13.6-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
          </svg>
          {busy === 'google' ? 'Opening Google…' : 'Continue with Google'}
        </button>

        {!showPasswordForm ? (
          <button className="btn ghost" onClick={() => setShowPasswordForm(true)} disabled={busy !== null}>
            Use Email and Password Instead
          </button>
        ) : (
          <>
            <div className="divider">OR</div>
            <form onSubmit={handlePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label className="field">
                <span className="lab">Email</span>
                <input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={`you@${TEAM_EMAIL_DOMAIN}`}
                  required
                />
              </label>
              <label className="field">
                <span className="lab">Password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
              <button className="btn primary" type="submit" disabled={busy !== null}>
                {busy === 'password' ? 'Signing In…' : 'Sign In'}
              </button>
            </form>
          </>
        )}

        {error && <div className="auth-error">{error}</div>}

        <div className="auth-foot">
          Everyone on the Decked Media team can sign in and edit every series — there are no
          per-role restrictions. If your account is not recognised, ask Team to add you to the
          roster.
        </div>
      </div>
    </div>
  );
}
